import { useLayoutEffect, useState } from 'react'
import isNil from 'lodash-es/isNil'

import { EMPTY_STRING } from '../../../constants/common'
import { HTML_DATA_PARAM_FILE_PREVIEW_OPEN } from '../../../constants/html'
import { isEventTargetSelf, stopPropagation } from '../../../utils/events'

/**
 * Owns the whole gallery presence cycle: open → exit animation → unmount.
 *
 * `handleClose` starts the exit animation instead of closing right away;
 * `handleAnimationEnd` finishes it (calls `onClose` once the overlay's own
 * animation ends), so the preview stays mounted and fades out as a whole. While
 * open, the body flag marks the preview as active.
 */
export function useFilesPreviewGalleryLifecycle({ initActive, onClose }) {
  const isOpen = !isNil(initActive)

  const [isExiting, setIsExiting] = useState(false)

  useLayoutEffect(() => {
    if (isOpen) {
      document.body.setAttribute(
        HTML_DATA_PARAM_FILE_PREVIEW_OPEN,
        EMPTY_STRING
      )

      return () => {
        document.body.removeAttribute(HTML_DATA_PARAM_FILE_PREVIEW_OPEN)
      }
    }
  }, [isOpen])

  function handleClose() {
    setIsExiting(true)
  }

  function handleClickBackdrop(event) {
    stopPropagation(event)

    if (isEventTargetSelf(event)) {
      handleClose()
    }
  }

  function handleAnimationEnd(event) {
    // Ignore the enter animation and anything bubbling up from descendants.
    if (isExiting && isEventTargetSelf(event)) {
      setIsExiting(false)

      onClose()
    }
  }

  return {
    handleAnimationEnd,
    handleClickBackdrop,
    handleClose,
    isExiting,
    isOpen,
  }
}
